import { useCallback } from "react";
import { format as originalFormat } from "date-fns/format";
import { formatDistance as originalFormatDistance } from "date-fns/formatDistance";
import { formatDistanceStrict as originalFormatDistanceStrict } from "date-fns/formatDistanceStrict";
import { formatDistanceToNow as originalFormatDistanceToNow } from "date-fns/formatDistanceToNow";
import { formatDistanceToNowStrict as originalFormatDistanceToNowStrict } from "date-fns/formatDistanceToNowStrict";
import { formatDuration as originalFormatDuration } from "date-fns/formatDuration";
import { formatRelative as originalFormatRelative } from "date-fns/formatRelative";
import { formatInTimeZone as originalFormatInTimeZone } from "date-fns-tz/formatInTimeZone";
import { useDateFnsLocale } from "./useDateFnsLocale";

export const useDateFnsFormattersWithLocale = () => {
  const localeFallback = useDateFnsLocale();

  const format = useCallback<typeof originalFormat>(
    (date, formatStr, { locale = localeFallback, ...options } = {}) => {
      return originalFormat(date, formatStr, { locale, ...options });
    },
    [localeFallback]
  );

  const formatDistance = useCallback<typeof originalFormatDistance>(
    (laterDate, earlierDate, { locale = localeFallback, ...options } = {}) => {
      return originalFormatDistance(laterDate, earlierDate, { locale, ...options });
    },
    [localeFallback]
  );

  const formatDistanceStrict = useCallback<typeof originalFormatDistanceStrict>(
    (laterDate, earlierDate, { locale = localeFallback, ...options } = {}) => {
      return originalFormatDistanceStrict(laterDate, earlierDate, { locale, ...options });
    },
    [localeFallback]
  );

  const formatDistanceToNow = useCallback<typeof originalFormatDistanceToNow>(
    (date, { locale = localeFallback, ...options } = {}) => {
      return originalFormatDistanceToNow(date, { locale, ...options });
    },
    [localeFallback]
  );

  const formatDistanceToNowStrict = useCallback<typeof originalFormatDistanceToNowStrict>(
    (date, { locale = localeFallback, ...options } = {}) => {
      return originalFormatDistanceToNowStrict(date, { locale, ...options });
    },
    [localeFallback]
  );

  const formatDuration = useCallback<typeof originalFormatDuration>(
    (duration, { locale = localeFallback, ...options } = {}) => {
      return originalFormatDuration(duration, { locale, ...options });
    },
    [localeFallback]
  );

  const formatRelative = useCallback<typeof originalFormatRelative>(
    (date, baseDate, { locale = localeFallback, ...options } = {}) => {
      return originalFormatRelative(date, baseDate, { locale, ...options });
    },
    [localeFallback]
  );

  const formatInTimeZone = useCallback<typeof originalFormatInTimeZone>(
    (date, timeZone, formatStr, { locale = localeFallback, ...options } = {}) => {
      return originalFormatInTimeZone(date, timeZone, formatStr, { locale, ...options });
    },
    [localeFallback]
  );

  return {
    format,
    formatDistance,
    formatDistanceStrict,
    formatDistanceToNow,
    formatDistanceToNowStrict,
    formatDuration,
    formatRelative,
    formatInTimeZone,
  };
};
